<template>
  <div class="row login-section justify-content-center align-items-center">
    <div class="col-md-8 col-lg-4">
      <div class="pj-card">
        <div class="pj-card-header">
          <h1 class="p-0 m-0">{{ t("SIGN_IN") }}</h1>
        </div>

        <!-- LOGIN FORM -->
        <div class="card-body mt-4">
          <Alert
            :alertIcon="newLoginAlertIcon"
            :header="newLoginAlertTitle"
            :message="newLoginAlertMessage"
            :alertType="newLoginAlertType"
            :closable="newLoginAlertClosable"
          />

          <div class="form-container">
            <form action="POST" @submit.prevent="validateAndSignIn">
              <div class="row">
                <div class="col-md-12">
                  <div class="form-group">
                    <!-- EMAIL -->
                    <div class="pb-2" v-if="loginToggle === 'email'">
                      <NewInputFieldEmail
                        :label-name="t('EMAIL_LABEL')"
                        ref="emailComponent"
                      />
                    </div>

                    <!-- PHONE -->
                    <div class="pb-2" v-else>
                      <NewInputFieldPhone
                        :label-name="t('PHONE_NUMBER_LABEL')"
                        ref="phoneComponent"
                      />
                    </div>

                    <!-- PASSWORD -->
                    <div class="pb-2">
                      <NewInputFieldPassword
                        :label-name="t('PASSWORD')"
                        :placeholder="t('PASSWORD')"
                        ref="passwordComponent"
                      />
                    </div>

                    <div class="pb-2 text-right">
                      <router-link
                        :to="{ name: 'ResetPasswordRequestView' }"
                        class="pj-link-green -align-center"
                      >
                        {{ t("FORGET_PASSWORD_TEXT") }}
                      </router-link>
                    </div>
                  </div>
                </div>

                <Alert
                  v-if="showAlert"
                  :message="alertMessage"
                  :alertType="alertType"
                  :closable="alertClosable"
                  @onClose="dismissAlert"
                />

                <Alert
                  v-if="showGeolocationAlert"
                  :message="geoLocationAlertMessage"
                  :alertType="geoLocationAlertType"
                  :closable="false"
                />

                <div class="col-md-12 mt-2">
                  <button
                    type="submit"
                    :disabled="loading || (requestFaceLogin && !geoLocation)"
                    class="button btn pj-wide pj-green-btn mt-2"
                  >
                    <i v-if="loading" class="fa fa-spin fa-spinner"></i>
                    <span v-else>{{ t("SIGN_IN") }}</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        <!-- LOGIN OPTIONS DIVIDER -->
        <div class="login-methods-divider">
          <span> {{ t("LOGIN_WITH_OPTIONS_LABEL") }} </span>
        </div>

        <!-- LOGIN METHOD TOGGLE -->
        <div class="row justify-content-center login-methods">
          <div class="col-12 col-md-6">
            <button
              class="button email_mode btn pj-wide"
              v-if="loginToggle === 'phone_number'"
              :class="loginToggle === 'email' ? 'active-mode' : ''"
              @click="loginToggle = 'email'"
            >
              {{ t("LOGIN_EMAIL_LABEL") }}
            </button>

            <button
              class="button phone_mode btn pj-wide"
              v-if="loginToggle === 'email'"
              :class="loginToggle === 'phone_number' ? 'active-mode' : ''"
              @click="loginToggle = 'phone_number'"
            >
              {{ t("LOGIN_PHONE_LABEL") }}
            </button>
          </div>
          <div class="col-12 col-md-6">
            <button class="button btn pj-wide" @click="redirectToPinLogin">
              PIN
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useI18n } from "@/apps/person-registration/login/composables/useI18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import Alert from "@/apps/person-registration/shared-components/common/Alert.vue";
import AuthenticationService from "@/apps/merchant3/services/merchant.login.service";
import PersonAuthenticationService from "@/apps/person-registration/services/authentication.service";
import NewInputFieldEmail from "@/apps/person-registration/shared-components/input-fields/NewInputFieldEmail.vue";
import NewInputFieldPassword from "@/apps/person-registration/shared-components/input-fields/NewInputFieldPassword.vue";
import NewInputFieldPhone from "@/apps/person-registration/shared-components/input-fields/NewInputFieldPhone.vue";
import { IPersonLoginData } from "@/apps/merchant3/types/IPersonLoginData";
import { getMerchant } from "@/apps/merchant3/services/merchant.service";
import EventLogs from "@/apps/merchant3/types/EventLogs";
import logEvent from "@/apps/merchant3/services/event-logs.service";
import AvailableFeatureFlags from "@/apps/merchant3/types/AvailableFeatureFlags";
import MerchantFlags from "@/apps/merchant3/types/MerchantFlags";
import { getCountryCode } from "@/apps/merchant3/helpers/country";

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const loginToggle = ref("email");

const phoneComponent = ref();
const emailComponent = ref();
const passwordComponent = ref();

const loading = ref(false);
const newLoginAlertIcon = ref("fa-solid fa-wand-magic-sparkles");
const newLoginAlertTitle = ref(t("NEW_CLERK_LOGIN_ALERT_TITLE"));
const newLoginAlertMessage = ref(t("NEW_CLERK_LOGIN_ALERT_CONTENT"));
const newLoginAlertType = ref("alert-success");
const newLoginAlertClosable = ref(false);
const alertType = ref("");
const alertMessage = ref("");
const showAlert = ref(false);
const alertClosable = ref(true);
const geoLocation = ref();
const showGeolocationAlert = ref(false);
const geoLocationAlertMessage = ref(t("PLEASE_ALLOW_ACCESS_LOCATION"));
const geoLocationAlertType = "alert-danger";
const redirectURL =
  router.currentRoute.value.query.afterRegistration ??
  router.currentRoute.value.query.afterLogin ??
  null;

const requestFaceLogin = ref(false);

document.title = "PayJoy - " + t("PAGE_TITLE_LOGIN");

onMounted(async () => {
  await getMerchant()
    .then((response) => {
      const merchantData = response.data;

      requireFaceLogin(
        merchantData.merchant?.country,
        merchantData.merchantFlags ?? [],
        merchantData.featureFlags ?? {}
      );
    })
    .catch(() => {
      logEvent(EventLogs.Events.MERCHANT_DETAILS_FETCH_FAILED);
    });
});

/**
 * This code is pretty much copied from "face-login.service.ts"; this logic will be extracted
 * from FE apps and put in BE.
 */
const requireFaceLogin = (
  merchantCountry: string,
  merchantFlags: string[],
  featureFlags: Record<string, boolean>
): void => {
  const isFeatureFlagEnabled =
    typeof featureFlags[AvailableFeatureFlags.CLERK_2FA_TOGGLE] !== "undefined";

  if (!isFeatureFlagEnabled) {
    return;
  }

  const faceLoginCountriesEnabled = ["CO", "ZA"];
  const geoLocationFlags = [
    MerchantFlags.USE_GEO_BLOCK,
    MerchantFlags.USE_GEO_BLOCK_SALES_OUTSIDE_STORE,
    MerchantFlags.USE_GEOFENCE,
  ];

  const isInFaceLoginCountry = faceLoginCountriesEnabled.some(
    (country) => country.toLowerCase() === merchantCountry.toLowerCase()
  );
  const hasRequiredFlags = geoLocationFlags.some((flag) =>
    merchantFlags.includes(flag)
  );

  requestFaceLogin.value = isInFaceLoginCountry && hasRequiredFlags;

  if (!requestFaceLogin.value) {
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (location) => {
      showAlert.value = false;

      geoLocation.value = {
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
        locationAccuracy: location.coords.accuracy,
      };
    },
    (e) => {
      showGeolocationAlert.value = e.code === e.PERMISSION_DENIED;
    }
  );
};

const dismissAlert = () => {
  showAlert.value = false;
};

const redirectToPinLogin = () => {
  window.location.href = `${process.env.VUE_APP_APP_URL}/${getCountryCode()}/store/clerk-login`;
};

const validateAndSignIn = () => {
  (loginToggle.value === "email"
    ? emailComponent.value.validate()
    : phoneComponent.value.validate()) &&
    passwordComponent.value.validate() &&
    signIn();
};

const signIn = async () => {
  const personAuthService = new PersonAuthenticationService();
  let password = passwordComponent.value.getInputFieldValue();
  const username =
    loginToggle.value === "email"
      ? emailComponent.value.getInputFieldValue()
      : phoneComponent.value.getInputFieldValue();

  let loginPayload: IPersonLoginData = {
    username: username,
    password: password,
  };

  let geoData;

  if (requestFaceLogin.value) {
    geoData = {
      country: store.state.merchant?.merchant?.country,
      latitude: geoLocation.value.latitude,
      longitude: geoLocation.value.longitude,
      locationAccuracy: geoLocation.value.locationAccuracy,
    };

    loginPayload = {
      ...loginPayload,
      ...geoData,
    };

    logEvent(EventLogs.Events.FACE_LOGIN_REQUIRED, {
      salesClerkUsername: loginPayload.username,
      origin: "PersonLogin",
    });
  }

  dismissAlert();
  loading.value = true;

  try {
    const data = await personAuthService.signIn(loginPayload);
    const personJwt = data.accessToken;
    // LOGIN PERSON AS CLERK

    if (!redirectURL) {
      alertType.value = "alert-danger";
      alertMessage.value = t("API_AUTH_PERSON_INCOMPLETE_REGISTRATION");
      showAlert.value = true;
      return;
    }

    const authenticationService = new AuthenticationService();

    sessionStorage.setItem("PJ_accessToken", personJwt);

    const merchantId = sessionStorage.getItem("PJ_merchant");
    let clerkPersonLoginPayload = { merchantId: merchantId };

    clerkPersonLoginPayload = {
      ...clerkPersonLoginPayload,
      ...geoData,
    };

    authenticationService
      .clerkAsPersonLogin(personJwt, clerkPersonLoginPayload)
      .then((data) => {
        sessionStorage.setItem("PJ_ClerkJwt", data.jwt);
        sessionStorage.setItem("PJ_clerk", data.clerk.salesClerkId.toString());

        window.location.href = `${redirectURL}`;
      })
      .catch((error) => {
        console.log(error.response?.data);
      });

    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  } catch (error: any) {
    let message = t("ERROR_SOMETHING_GOES_WRONG");

    switch (error.response?.data.code) {
      case "API-AUTH-ACCOUNT-DEACTIVATED":
        message = t("API_AUTH_ACCOUNT_DEACTIVATED");
        break;
      case "API-AUTH-PERSON-INVALID-CREDENTIALS":
        message = t("API_AUTH_PERSON_INVALID_CREDENTIALS");
        break;
      case "API-AUTH-PERSON-INCOMPLETE-REGISTRATION":
        message = t("API_AUTH_PERSON_INCOMPLETE_REGISTRATION");
        break;
      case "API-AUTH-PERSON-PASSWORD-RESET-REQUIRED":
        message = t("API_AUTH_PERSON_PASSWORD_RESET_REQUIRED");
        break;
      case "API-AUTH-PERSON-NOT-VERIFIED": {
        const messageKey =
          loginToggle.value === "email"
            ? "API_AUTH_PERSON_NOT_VERIFIED_EMAIL"
            : "API_AUTH_PERSON_NOT_VERIFIED_PHONE";
        message = t(messageKey);
        break;
      }
    }

    alertType.value = "alert-danger";
    alertMessage.value = message;
    showAlert.value = true;
  } finally {
    loading.value = false;
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

h1,
h2,
h3,
h4 {
  color: $pj-color-green;
  font-weight: 600;
}

.active-mode {
  color: #ffffff;
  background-color: $pj-color-yellow-green;
}

.pj-login-mode {
  border-radius: 30px;
  border: 1px solid $pj-color-silver-sand;

  @media screen and (max-width: $mobile) {
    margin: 0px 20px;
  }

  .row {
    .col {
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
}

.login-mode {
  border-radius: 30px;
  padding: 15px;
  text-align: center;
  cursor: pointer;
}

.pj-card-header {
  text-align: center;
}

.login-section {
  font-family: "Inter";
  height: 80vh;
}

// New styles

.btn.pj-green-btn {
  font-size: 1rem;
}

.btn.pj-green-btn {
  border-radius: 5px;
}

.login-methods-divider {
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin: 2rem 0;

  &:before {
    content: "";
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 1px;
    background: $pj-color-gray;
  }

  > span {
    display: block;
    position: relative;
    margin: 0 auto;
    font-size: 0.8rem;
    color: $pj-color-gray;
    padding: 0 1rem;
    background-color: #ffffff;
  }
}

.login-methods {
  $maxMobile: calc($tablets - 1px);
  @media screen and (max-width: $tablets) {
    .col-12 {
      margin: 0.54rem auto;
    }
  }
  .btn {
    width: 100%;
    padding: 10px 15px;
    font-size: 1rem;
    color: #fff;
    background-color: $pj-color-yellow-green;
  }
}
</style>
